export const applyGpuFilter = (
    source: HTMLImageElement | HTMLCanvasElement,
    destinationContext: any,
    gl: WebGLRenderingContext,
    width: number,
    height: number,
    filter: string
): void => {

    // Initialize shaders
    const program = createWebGLProgram(gl, filter, width, height);

    // Create buffers to place vertex data onto the GPU.
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, 1, -1, -1, 1, -1, 1, 1]), gl.STATIC_DRAW);

    // These coordinates are used to supply vertices to the vertex shader.
    const vector2DCoordinates = gl.getAttribLocation(program, 'vector2DCoordinates');
    gl.vertexAttribPointer(vector2DCoordinates, 2, gl.FLOAT, false, 0, 0);
    gl.enableVertexAttribArray(vector2DCoordinates);

    // Register the texture to use and apply the source image to the texture.
    gl.activeTexture(gl.TEXTURE0);
    const texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);

    // Place the filtered image on the whole screen.
    // `S` and `T` can be seen as the `X` and `Y` axis.
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);

    // Specify where in the GPUs memory the uniform variable is located.
    gl.uniform1i(gl.getUniformLocation(program, 'vector2DCoordinates'), 0);

    // Render the primitive shape on which the filtered image is placed.
    gl.drawArrays(gl.TRIANGLE_FAN, 0, 4);

    // Render filtered image on destination.
    destinationContext.drawImage(gl.canvas, 0, 0, source.width, source.height);
};

const createWebGLProgram = (
    gl: WebGLRenderingContext,
    filter: string,
    width: number,
    height: number
): WebGLProgram => {

    const program = gl.createProgram()!;

    // A vertex shader can manipulate the attributes of vertices, which are corner points of polygons.
    const vertexShader = gl.createShader(gl.VERTEX_SHADER)!;
    gl.shaderSource(vertexShader, webGLVertexShaderCode());
    gl.compileShader(vertexShader);
    gl.attachShader(program, vertexShader);

    // A fragment shader takes care of how the pixels between the vertices look.
    const fragmentShader = gl.createShader(gl.FRAGMENT_SHADER)!;
    gl.shaderSource(fragmentShader, webGLFragmentShaderCode(filter, width, height));
    gl.compileShader(fragmentShader);
    gl.attachShader(program, fragmentShader);

    gl.linkProgram(program);
    gl.useProgram(program);

    return program;
};

const webGLVertexShaderCode = (): string => {
    return `
        attribute vec2 vector2DCoordinates;
        void main() {
            gl_Position=vec4(vector2DCoordinates, 0.0, 1.0);
        }`;
};

const webGLFragmentShaderCode = (
    filter: string, width: number, height: number
): string => {

    // This is a single pixel in RGBA.
    let fragColor = 'gl_FragColor = vec4(vec3(color.r+color.g+color.b), color.a););';

    if (filter === 'grayscale') {
        fragColor = 'gl_FragColor = vec4(vec3(color.r+color.g+color.b)/3.0, color.a);';
    }

    return `
        uniform sampler2D texture;
        void main() {
            highp vec2 textureCoordinates =
                vec2(gl_FragCoord.x/${width}.0, 1.0 - (gl_FragCoord.y/${height}.0));
            highp vec4 color = texture2D(texture, textureCoordinates);
            ${fragColor}
        }`;
};